import React, { useContext, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import noop from 'lodash/noop';

import { namedRoute } from '../utils/helpers/routes';

let errorCounter = 0;

enum ErrorType {
  Client = 'Client',
  Internal_API = 'Internal_API',
  External_API = 'External_API',
}

export class InnerApiCallError extends Error {
  type: ErrorType;

  constructor(message?) {
    super(message);
    this.type = ErrorType.Internal_API;
  }
}

export class ExternalApiCallError extends Error {
  type: ErrorType;

  constructor(message?) {
    super(message);
    this.type = ErrorType.External_API;
  }
}

export class ClientError extends Error {
  type: ErrorType;

  constructor(message?) {
    super(message);
    this.type = ErrorType.Client;
  }
}

export type CustomError =
  | ClientError
  | ExternalApiCallError
  | InnerApiCallError;

export interface AppError {
  id: number;
  error: CustomError;
}

export interface AppErrorContext {
  appErrors: AppError[];
  addAppError: (error: CustomError) => number | void;
  removeAppError: (errorId: number) => void;
  clearErrors: () => void;
  showErrorPage: (error: CustomError) => void;
}

const context = React.createContext<AppErrorContext>({
  appErrors: [],
  addAppError: noop,
  removeAppError: noop,
  clearErrors: noop,
  showErrorPage: noop,
});

const AppErrorProvider = ({ children }: { children? }) => {
  const [appErrors, setErrors] = useState<AppError[]>([]);
  const history = useHistory();

  const addAppError = useCallback(
    (newError: CustomError) => {
      const errorId = errorCounter++;

      setErrors([
        ...appErrors,
        {
          id: errorId,
          error: newError,
        },
      ]);

      return errorId;
    },
    [appErrors, setErrors],
  );

  const removeAppError = useCallback(
    (errorId) => {
      setErrors(appErrors.filter(({ id }) => id !== errorId));
    },
    [appErrors, setErrors],
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  const showErrorPage = useCallback(
    (error) => {
      setErrors([{ id: errorCounter++, error }]);
      history.push(namedRoute('error'));
    },
    [history, setErrors],
  );

  return (
    <context.Provider
      value={{
        appErrors,
        addAppError,
        removeAppError,
        clearErrors,
        showErrorPage,
      }}
    >
      {children}
    </context.Provider>
  );
};

const useAppErrors = () => useContext(context);

const { Consumer: AppErrorConsumer } = context;

export { AppErrorConsumer, AppErrorProvider, useAppErrors, ErrorType };
